import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, formatApiError } from "../../lib/api";

const peso = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });
const pesoFull = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERIODS = [["monthly", "Monthly"], ["quarterly", "Quarterly"], ["ytd", "Year to Date"]];
const PIE = ["#38bdf8", "#34d399", "#a78bfa", "#f472b6", "#fbbf24", "#60a5fa", "#fb7185", "#22d3ee", "#c084fc"];

export default function CrmAnalytics() {
    const [period, setPeriod] = useState("monthly");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [sort, setSort] = useState({ key: "leads", dir: "desc" });
    const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true); setErrored(false);
            try {
                const res = await api.get("/crm/analytics", { params: { period } });
                if (alive) setData(res.data && typeof res.data === "object" ? res.data : null);
            } catch (e) { if (alive) { setErrored(true); toast.error(formatApiError(e)); } }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [period]);

    const sum = data?.summary || {};
    const source = (data?.lead_source || []).map((s, i) => ({ name: s.source, value: s.value, count: s.count, fill: PIE[i % PIE.length] }));
    const status = (data?.client_status || []).map((s, i) => ({ name: s.status, value: s.count, fill: PIE[i % PIE.length] }));
    const payment = (data?.payment_mode || []).map((p) => ({ name: p.mode, value: p.value }));
    const aging = data?.aging || {};
    const agents = [...(data?.agent_conversions || [])].sort((a, b) => {
        const d = (a[sort.key] || 0) - (b[sort.key] || 0);
        return sort.dir === "asc" ? d : -d;
    });
    const srcTotal = source.reduce((a, b) => a + b.value, 0) || 1;
    const sortArrow = (key) => (sort.key === key ? (sort.dir === "desc" ? " ↓" : " ↑") : "");

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-analytics-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Sales Analytics</h1>
                    {data && <div className="text-xs text-muted-foreground mt-1">{data.date_from} → {data.date_to}</div>}
                </div>
                <div className="inline-flex rounded-md border border-border overflow-hidden self-start">
                    {PERIODS.map(([v, l], i) => (
                        <button key={v} onClick={() => setPeriod(v)}
                            className={`px-3 py-1.5 text-sm ${i > 0 ? "border-l border-border" : ""} ${period === v ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                            data-testid={`analytics-period-${v}`}>{l}</button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Leads" value={loading ? "…" : sum.leads} />
                <Stat label="New" value={loading ? "…" : sum.new} />
                <Stat label="Qualified" value={loading ? "…" : sum.qualified} />
                <Stat label="Converted" value={loading ? "…" : `${sum.converted} (${sum.conversion_rate ?? 0}%)`} />
                <Stat label="Sales Value" value={loading ? "…" : peso(sum.total_value)} />
            </div>

            {!loading && !errored && sum.leads === 0 && (
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">No leads in this period.</div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Lead source breakdown by value */}
                <Panel title="Lead Source Breakdown" subtitle="by sales value">
                    {source.length === 0 ? <Empty /> : (
                        <div className="flex flex-col sm:flex-row items-center gap-3">
                            <div style={{ width: 200, height: 200 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={source} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={0}>
                                            {source.map((s, i) => <Cell key={i} fill={s.fill} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => pesoFull(v)} contentStyle={tip} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-1.5 w-full">
                                {source.map((s) => (
                                    <div key={s.name} className="flex items-center gap-2 text-sm">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.fill }} />
                                        <span className="flex-1 truncate">{s.name}</span>
                                        <span className="font-medium tabular-nums">{peso(s.value)}</span>
                                        <span className="text-xs text-muted-foreground w-12 text-right">{((s.value / srcTotal) * 100).toFixed(1)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Panel>

                {/* Client status */}
                <Panel title="Client Status" subtitle="lead count by status">
                    {status.length === 0 ? <Empty /> : (
                        <div className="flex flex-col sm:flex-row items-center gap-3">
                            <div style={{ width: 200, height: 200 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={50}>
                                            {status.map((s, i) => <Cell key={i} fill={s.fill} />)}
                                        </Pie>
                                        <Tooltip contentStyle={tip} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-1.5 w-full">
                                {status.map((s) => (
                                    <div key={s.name} className="flex items-center gap-2 text-sm">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.fill }} />
                                        <span className="flex-1 truncate">{s.name}</span>
                                        <span className="font-medium tabular-nums">{s.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Panel>
            </div>

            {/* Mode of payment */}
            <Panel title="Mode of Payment" subtitle="sales value by payment method">
                {payment.length === 0 ? <Empty text="No payment data for this period." /> : (
                    <div style={{ width: "100%", height: Math.max(180, payment.length * 42) }}>
                        <ResponsiveContainer>
                            <BarChart data={payment} layout="vertical" margin={{ left: 20, right: 60 }}>
                                <XAxis type="number" tickFormatter={peso} tick={{ fontSize: 10, fill: "currentColor", opacity: .5 }} axisLine={false} tickLine={false} />
                                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "currentColor", opacity: .7 }} axisLine={false} tickLine={false} />
                                <Tooltip formatter={(v) => pesoFull(v)} contentStyle={tip} cursor={{ fill: "currentColor", opacity: .05 }} />
                                <Bar dataKey="value" fill="#38bdf8" radius={[0, 4, 4, 0]} barSize={22} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Panel>

            {/* New vs aging */}
            <Panel title="Leads — New vs Aging" subtitle="new this period, and how old the open leads are">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <MiniStat label={`New (${period})`} value={aging.new_this_period ?? 0} cls="text-sky-600" />
                    <MiniStat label="Fresh · ≤30d" value={aging.fresh ?? 0} cls="text-emerald-600" />
                    <MiniStat label="Aging · 31–90d" value={aging.aging ?? 0} cls="text-amber-600" />
                    <MiniStat label="Stale · 90+d" value={aging.stale ?? 0} cls="text-rose-600" />
                </div>
            </Panel>

            {/* Agent leads vs conversions */}
            <Panel title="Agent — Leads vs Conversions" subtitle="this period">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[600px]">
                        <thead><tr>
                            <th>Agent</th>
                            <th className="text-right cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("leads")}>Leads{sortArrow("leads")}</th>
                            <th className="text-right cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("converted")}>Converted{sortArrow("converted")}</th>
                            <th className="w-1/3 cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("conversion_rate")}>Conversion Rate{sortArrow("conversion_rate")}</th>
                        </tr></thead>
                        <tbody>
                            {agents.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No agents in this period.</td></tr>}
                            {agents.map((a) => (
                                <tr key={a.agent}>
                                    <td className="font-medium">{a.agent}</td>
                                    <td className="sc-num">{a.leads}</td>
                                    <td className="sc-num text-emerald-600">{a.converted}</td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                                                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${Math.min(100, a.conversion_rate)}%` }} />
                                            </div>
                                            <span className="text-xs font-medium w-12 text-right">{a.conversion_rate}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <p className="text-xs text-muted-foreground">"Value" = each lead's Sales Amount (Lark import). Aging is a live snapshot of all open leads. For real invoiced revenue see Team Performance → Sales (SAP).</p>
        </div>
    );
}

const tip = { background: "var(--card, #0b1220)", border: "1px solid rgba(148,163,184,.3)", borderRadius: 8, fontSize: 12 };
function Panel({ title, subtitle, children }) {
    return (
        <div className="sc-card p-4">
            <div className="mb-3"><div className="text-sm font-semibold">{title}</div>{subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}</div>
            {children}
        </div>
    );
}
function Stat({ label, value }) {
    return <div className="sc-card p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-lg font-semibold mt-1">{value}</div></div>;
}
function MiniStat({ label, value, cls = "" }) {
    return <div className="rounded-md border border-border px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`text-xl font-semibold mt-0.5 ${cls}`}>{value}</div></div>;
}
function Empty({ text = "No data." }) {
    return <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">{text}</div>;
}
